import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, TextField, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import jsPDF from 'jspdf';
import { saveAs } from 'file-saver';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxWidth: 800,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflowY: 'auto',
};

const ContractEditor = ({ open, onClose, rentalData, placeDescription, contractText, onContractUpdate }) => {
  const [editedText, setEditedText] = useState('');

  const generateDefaultContract = () => {
    return `
TENANCY AGREEMENT

This Tenancy Agreement is made on ${new Date().toLocaleDateString()} between:

Landlord: [Your Name/Company Name]
Address: ${placeDescription || 'Not specified'}

And

Tenant: ${rentalData?.tenant_name || '[Tenant Name]'}
Address: [Tenant Address]
Phone: ${rentalData?.tenant_phone || '[Tenant Phone]'}
Email: ${rentalData?.tenant_email || '[Tenant Email]'}

PROPERTY
The Landlord agrees to rent to the Tenant the property located at: ${placeDescription || '[Property Address]'}

TERM
The tenancy begins on ${rentalData?.rent_start || '[Start Date]'} and ends on ${rentalData?.rent_end || '[End Date]'}

RENT
The Tenant agrees to pay ${rentalData?.rent_currency || '[Currency]'} ${rentalData?.rent_amount || '[Amount]'} per month, payable on the first day of each month.

TERMS AND CONDITIONS
1. The Tenant will maintain the property in good condition.
2. The Landlord may enter the property with 24 hours notice for inspections.
3. [Add additional terms as needed]

SIGNATURES
Landlord: ___________________________    Date: _____________
Tenant: _____________________________    Date: _____________
    `;
  };

  useEffect(() => {
    if (rentalData) {
      setEditedText(contractText || generateDefaultContract());
    }
  }, [rentalData, contractText]);

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setEditedText(newText);
    onContractUpdate(newText); // Update parent state as user types
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(12);
    doc.text(editedText, 10, 10, { maxWidth: 190 });
    doc.save(`tenancy_agreement_${rentalData?.tenant_name || 'contract'}.pdf`);
  };

  const handleDownloadDoc = () => {
    const blob = new Blob([editedText], { type: 'application/msword' });
    saveAs(blob, `tenancy_agreement_${rentalData?.tenant_name || 'contract'}.doc`);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="contract-editor-title"
      aria-describedby="contract-editor-description"
    >
      <Box sx={style}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography id="contract-editor-title" variant="h6" component="h2">
            Edit Tenancy Agreement
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <TextField
          fullWidth
          multiline
          rows={20}
          value={editedText}
          onChange={handleTextChange}
          variant="outlined"
          label="Contract Text"
          sx={{ mb: 2 }}
        />
        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleDownloadPDF}
          >
            Download PDF
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<DescriptionIcon />}
            onClick={handleDownloadDoc}
          >
            Download DOC
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ContractEditor;